import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function ManageAllergiesModal({ serverId, serviceUserId, onClose }) {
    const [allergies, setAllergies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAllergies = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'allergies');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Sort by active status first, then by name
                loaded.sort((a, b) => {
                    if (a.active !== b.active) return a.active ? -1 : 1;
                    return (a.name || '').localeCompare(b.name || '');
                });
                
                setAllergies(loaded);
            } catch (err) {
                console.error("Error fetching allergies:", err);
                setError("Failed to load allergies.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchAllergies();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Manage Allergies</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Active and archived allergies/intolerances</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading allergies...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : allergies.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Allergies</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>No allergies or intolerances have been recorded.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {allergies.map((allergy, index) => (
                                <div key={allergy.id || index} style={{...styles.recordCard, opacity: allergy.active ? 1 : 0.6}}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{allergy.name}</h3>
                                            <span style={{ 
                                                display: 'inline-block', 
                                                marginTop: '6px',
                                                padding: '4px 10px', 
                                                borderRadius: '9999px', 
                                                fontSize: '11px', 
                                                fontWeight: 'bold',
                                                backgroundColor: allergy.active ? '#fee2e2' : '#f3f4f6',
                                                color: allergy.active ? '#991b1b' : '#6b7280',
                                                border: `1px solid ${allergy.active ? '#fecaca' : '#d1d5db'}`
                                            }}>
                                                {allergy.active ? 'ACTIVE' : 'INACTIVE'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginTop: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                        <div style={{ flex: '1', minWidth: '200px' }}>
                                            <label style={styles.fieldLabel}>Notes / Reaction Details</label>
                                            <div style={styles.fieldValue}>{allergy.notes || 'No notes provided'}</div>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginTop: '16px' }}>
                                        <div style={{ flex: '1', minWidth: '150px' }}>
                                            <label style={styles.fieldLabel}>Start Date</label>
                                            <div style={styles.fieldValue}>{allergy.startDate ? new Date(allergy.startDate?.seconds ? allergy.startDate.seconds * 1000 : allergy.startDate).toLocaleDateString() : 'N/A'}</div>
                                        </div>
                                        <div style={{ flex: '1', minWidth: '150px' }}>
                                            <label style={styles.fieldLabel}>End Date</label>
                                            <div style={styles.fieldValue}>{allergy.endDate ? new Date(allergy.endDate?.seconds ? allergy.endDate.seconds * 1000 : allergy.endDate).toLocaleDateString() : 'Ongoing'}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' }
};
